import projections from './coordinateProjections';

/**
 * Importação da biblioteca leaflet a qual utiliza o OpenStreetMap,
 * é uma biblioteca de mapeamento leve e de codigo aberto, utiliza um banco
 * de dados geografico editável gratuito.
 */
import L from 'leaflet';
import proj4 from 'proj4';

/**
 * @class Coordinate
 *
 * @param measureString {string} É a medida em raw, pode vir com caracteres especiais
 * @param type {string} ['Grau/Min/Seg', 'Grau decimal', 'UTM']
 */
export default class Coordinate {
  /**
   *
   * @param {Array} coordinates Array de coordenadas. [x, y] ou [long, lat]
   * @param {'DD'|'DMS'|'UTM'} type
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} projection
   */
  constructor(coordinates, zone, type, projection = 'WGS84') {
    /**
     * Formatos padrões
     *
     * dd (decimal degrees): ±99.99999
     * dms (degree minute second): ±180° 60' 60"
     *
     * http://www.dpi.inpe.br/calcula/
     */
    this.coordinates = [
      coordinates[0].replace(',', '.'),
      coordinates[1].replace(',', '.'),
    ];
    this.zone = zone;
    this.type = type;
    this.projection = projection;
  }

  /**
   * Conversão de coordenadas de Grau Décimal para Grau Minuto Segundo
   *
   * @param {Array} coordinates Coordenadas em Grau Decimal. [long, lat]
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} fromProjection
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} toProjection
   * @returns Coordenadas em Grau Minuto Segundo. [long, lat]
   */
  static convertDDToDMS( // transforma grau decimal em grau minuto segundo
    coordinates,
    fromProjection = 'WGS84',
    toProjection = 'WGS84'
  ) {
    // Método de convesão
    const convert = (dd) => {
      let negative = parseFloat(dd) < 0,
        coordinate = negative ? Math.abs(dd) : dd,
        degrees = parseInt(coordinate, 10),
        dec = ((coordinate - degrees) * 60).toFixed(5),
        minutes = parseInt(dec, 10),
        seconds = ((dec - minutes) * 60).toFixed(2);

      degrees = Math.abs(degrees);
      minutes = Math.abs(minutes);
      seconds = Math.abs(seconds);
      degrees = negative ? degrees * -1 : degrees;

      return L.Util.template('{d}° {m}\' {s}"', {
        d: degrees === 0 ? '00' : degrees,
        m: minutes === 0 ? '00' : minutes,
        s: seconds === 0 ? '00.00' : seconds,
      });
    };
    // Reprojeção da coordenada
    let coords = [coordinates[0], coordinates[1]];
    if (fromProjection !== toProjection) {
      coords = proj4(
        projections[fromProjection.toLowerCase()].geographic.getProjection(),
        projections[toProjection.toLowerCase()].geographic.getProjection(),
        [parseFloat(coords[0]), parseFloat(coords[1])]
      );
      coords[0] = coords[0].toFixed(7);
      coords[1] = coords[1].toFixed(7);
    }
    return [convert(coords[0]), convert(coords[1])];
  }

  /**
   * Conversão de coordenadas de Grau Minuto Segundo para Grau Décimal
   *
   * @param {Array} coordinates Coordenadas em Grau Minuto Segundo. [long, lat]
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} fromProjection
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} toProjection
   * @returns Coordenadas em Grau Decimal. [long, lat]
   */
  static convertDMSToDD( // conversão de grau minuto segundo para grau decimal
    coordinates,
    fromProjection = 'WGS84',
    toProjection = 'WGS84'
  ) {
    // Método de conversão
    const convert = (dms) => { // conversão de grau minuto segundo para grau decimal
      let parts = dms.split(/[^-\d.]+/), // separa a string em partes
        degrees = 0,
        minutes = 0,
        seconds = 0;

      if (parts[0]) {
        degrees = parseInt(parts[0], 10);
      }

      if (parts[1]) {
        minutes = parseInt(parts[1], 10) / 60;
        minutes = parseFloat(minutes.toFixed(7));
      }

      if (parts[2]) {
        seconds = parseFloat(parts[2]) / 3600;
        seconds = parseFloat(seconds.toFixed(7));
      }

      let dd = 0;
      dd =
        degrees > 0 ? degrees + minutes + seconds : degrees - minutes - seconds;

      return dd.toFixed(7);
    };
    // Reprojeção da coordenada
    let coords = [convert(coordinates[0]), convert(coordinates[1])];
    if (fromProjection !== toProjection) {
      coords = proj4(
        projections[fromProjection.toLowerCase()].geographic.getProjection(),
        projections[toProjection.toLowerCase()].geographic.getProjection(),
        [parseFloat(coords[0]), parseFloat(coords[1])]
      );
      coords[0] = coords[0].toFixed(7);
      coords[1] = coords[1].toFixed(7);
    }
    return coords;
  }

  /**
   * Conversão de coordenadas de Grau Décimal para UTM
   *
   * @param {Array} coordinates Coordenadas em Grau Decimal. [long, lat]
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} fromProjection
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} toProjection
   * @returns Coordenadas em UTM. {coords: [x, y], zone}
   */
  static convertDDtoUTM( // conversão de grau decimal para UTM
    coordinates,
    fromProjection = 'WGS84',
    toProjection = 'WGS84'
  ) {
    const geo2zoneUTM = (long) => {
      long = Number(long);
      long = long + 180;
      long = long / 6;
      return parseInt(long) + 1;
    };

    const [lng, lat] = coordinates;
    const zone = geo2zoneUTM(lng);

    let coords = proj4(
      projections[fromProjection.toLowerCase()].geographic.getProjection(),
      projections[toProjection.toLowerCase()].utm.getProjection(zone),
      [Number.parseFloat(lng), Number.parseFloat(lat)]
    );
    coords[0] = coords[0].toFixed(2);
    coords[1] = coords[1].toFixed(2);

    return { coords, zone };
  }

  /**
   * Conversão de coordenadas de UPM para Grau Décimal
   *
   * @param {Array} coordinates Coordenadas em UTM. [x, y]
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} fromProjection
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} toProjection
   * @returns Coordenadas em Grau Decimal. [long, lat]
   */
  static convertUTMtoDD( // conversão de UTM para grau decimal
    coordinates,
    zone,
    fromProjection = 'WGS84',
    toProjection = 'WGS84'
  ) {

    let coords = proj4(
      projections[fromProjection.toLowerCase()].utm.getProjection(zone),
      projections[toProjection.toLowerCase()].geographic.getProjection(),
      [parseFloat(coordinates[0]), parseFloat(coordinates[1])]
    );
    coords[0] = coords[0].toFixed(7);
    coords[1] = coords[1].toFixed(7);

    return coords;
  }

  /**
   * Coordenadas em Grau Décimal
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} projection
   * @returns Corrdenadas [long, lat]
   */
  getDD(projection) { // coordenadas em grau decimal
    switch (this.type) {
      case 'DD': {
        let coords;
        if (projection && projection !== this.projection) {
          coords = proj4(
            projections[
              this.projection.toLowerCase()
            ].geographic.getProjection(),
            projections[projection.toLowerCase()].geographic.getProjection(),
            [parseFloat(this.coordinates[0]), parseFloat(this.coordinates[1])]
          );
          coords[0] = coords[0].toFixed(7);
          coords[1] = coords[1].toFixed(7);
          return coords;
        }
        return this.coordinates;
      }
      case 'DMS':
        return Coordinate.convertDMSToDD( // conversão de grau minuto segundo para grau decimal
          this.coordinates,
          this.projection,
          projection ? projection : this.projection
        );
      case 'UTM':
        return Coordinate.convertUTMtoDD( // conversão de UTM para grau decimal
          this.coordinates,
          this.zone,
          this.projection, 
          projection ? projection : this.projection
        );
      default:
        return this.coordinates;
    }
  }

  /**
   * Corrdenadas em Grau Minuto Segundo
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} projection
   * @returns Corrdenadas [long, lat]
   */
  getDMS(projection) { 
    switch (this.type) {
      case 'DD':
        return Coordinate.convertDDToDMS(
          this.coordinates,
          this.projection,
          projection ? projection : this.projection
        );
      case 'DMS': {
        let coords;
        if (projection && projection !== this.projection) {
          coords = Coordinate.convertDMSToDD(
            this.coordinates,
            this.projection,
            this.projection
          );
          return Coordinate.convertDDToDMS(
            coords,
            this.projection,
            projection ? projection : this.projection
          );
        }
        return this.coordinates;
      }
      case 'UTM': {
        let coords = Coordinate.convertUTMtoDD(
          this.coordinates,
          this.zone,
          this.projection,
          this.projection
        );
        return Coordinate.convertDDToDMS(
          coords,
          this.projection,
          projection ? projection : this.projection
        );
      }
      default:
        return this.coordinates;
    }
  }

  /**
   * Coordenadas em UTM
   * @param {'WGS84'|'SIRGAS2000'|'SAD69'} projection
   * @returns Coordenadas em UTM. {coords: [x, y], zone}
   */
  getUTM(projection) { 
    switch (this.type) {
      case 'DD':
        return Coordinate.convertDDtoUTM(
          this.coordinates,
          this.projection,
          projection ? projection : this.projection
        );
      case 'DMS': {
        let coords = Coordinate.convertDMSToDD(
          this.coordinates,
          this.projection,
          this.projection
        );
        return Coordinate.convertDDtoUTM(
          coords,
          this.projection,
          projection ? projection : this.projection
        );
      }
      case 'UTM': {
        if (projection && projection !== this.projection) {
          let coords = proj4(
            projections[this.projection.toLowerCase()].utm.getProjection(
              this.zone
            ),
            projections[projection.toLowerCase()].utm.getProjection(this.zone),
            [parseFloat(this.coordinates[0]), parseFloat(this.coordinates[1])]
          );
          return {
            coords: [coords[0].toFixed(2), coords[1].toFixed(2)],
            zone: this.zone,
          };
        }
        return { coords: this.coordinates, zone: this.zone };
      }
      default:
        return { coords: this.coordinates, zone: this.zone };
    }
  }

  /**
   * Coodenada formatada de acordo com o tipo
   * @returns Coordenadas formatada. {coords: [x, y], zone}
   */
  toString() {
    switch (this.type) {
      case 'DD':
        return {
          coords: [
            `${parseFloat(this.coordinates[0]).toFixed(7)}`.replace('.', ','),
            `${parseFloat(this.coordinates[1]).toFixed(7)}`.replace('.', ','),
          ],
          zone: '',
        };
      case 'DMS':
        return {
          coords: [
            `${this.coordinates[0]}`.replace('.', ','),
            `${this.coordinates[1]}`.replace('.', ','),
          ],
          zone: '',
        };
      case 'UTM':
        return {
          coords: [
            `${this.coordinates[0]}`.replace('.', ','),
            `${this.coordinates[1]}`.replace('.', ','),
          ],
          zone: this.zone,
        };
      default:
        return {
          coords: [
            `${this.coordinates[0]}`.replace('.', ','),
            `${this.coordinates[1]}`.replace('.', ','),
          ],
          zone: this.zone,
        };
    }
  }

  /**
   * Converte coordenadas para o formato WKT
   * @param {Array} coordinates Coordenadas em GD. [[x, y], [n, m], ..., [w, r]]
   * @returns Coordenadas WKT. '(x y, n m, ..., w r)'
   */
  static convertCoordinatesToWKT(coordinates, withParentheses = true) {
    let wktCoordinates = '';
    // let strWktCoordinates = '';
    
    coordinates.forEach((coord) => {
      wktCoordinates = wktCoordinates + `${coord[0]} ${coord[1]}, `
    });
    wktCoordinates = withParentheses
      ? "(" + wktCoordinates.slice(0, -2) + ")"
      : wktCoordinates.slice(0, -2);
    return wktCoordinates;
  }
}
